import { Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { PathsOf } from '@/types/subform';

import { ExportEntityBundleModalFormShape } from './ExportEntityBundleModal.types';
import { getPathToExport } from './ExportEntityBundleModal.utils';

export interface ExportEntityBundleModalProps {
  entityId: string;
  householdId: string;
  deathOrderId?: string | null | undefined;
  onClose: () => void;
  onExport: (pathToPrint: string) => void;
  exportLoading: boolean;
  includeNotesOnDiagramSlide?: boolean;
}

export function ExportEntityBundleModal({
  entityId,
  householdId,
  deathOrderId,
  onClose,
  onExport: onExportExternal,
  exportLoading,
  includeNotesOnDiagramSlide = true,
}: ExportEntityBundleModalProps) {
  const form = useForm<ExportEntityBundleModalFormShape>({
    defaultValues: {
      includeCoverSlide: true,
      includeDiagramAndNotesSlide: true,
      includeBalanceSheetSlide: true,
      includeBeneficiariesAndDispositionsSlide: true,
      includeNotesOnDiagramSlide,
      includeInsurancePolicySlides: false,
    },
  });

  const onExport = form.handleSubmit((formData) => {
    const pathToPrint = getPathToExport(
      entityId,
      householdId,
      deathOrderId,
      formData
    );
    onExportExternal(pathToPrint);
    onClose();
  });

  return (
    <DialogModal
      isOpen
      onClose={onClose}
      heading="Export PDF"
      dialogContentSx={{
        minWidth: 600,
        py: 0,
        pb: 3,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2}>
          <ExportEntityBundleModalInner />
          <Button
            variant="primary"
            size="sm"
            onClick={onExport}
            fullWidth
            loading={exportLoading}
          >
            Export
          </Button>
        </Stack>
      </FormProvider>
    </DialogModal>
  );
}

function ExportEntityBundleModalInner() {
  const { control } = useFormContext<ExportEntityBundleModalFormShape>();

  return (
    <Stack spacing={2}>
      <Card variant="filled" sx={{ p: 2, pb: 4 }}>
        <Stack spacing={2}>
          <Typography variant="title-l">Pages to include</Typography>
          <FormLayoutRow>
            <FormLayoutItem>
              <FormAwareCheckbox<ExportEntityBundleModalFormShape>
                control={control}
                fieldName={
                  'includeCoverSlide' as const satisfies PathsOf<ExportEntityBundleModalFormShape>
                }
                label="Cover"
              />
              <FormAwareCheckbox<ExportEntityBundleModalFormShape>
                control={control}
                fieldName={
                  'includeDiagramAndNotesSlide' as const satisfies PathsOf<ExportEntityBundleModalFormShape>
                }
                label="Diagram"
              />
              <FormAwareCheckbox<ExportEntityBundleModalFormShape>
                control={control}
                fieldName={
                  'includeBalanceSheetSlide' as const satisfies PathsOf<ExportEntityBundleModalFormShape>
                }
                label="Balance sheet"
              />
              <FormAwareCheckbox<ExportEntityBundleModalFormShape>
                control={control}
                fieldName={
                  'includeBeneficiariesAndDispositionsSlide' as const satisfies PathsOf<ExportEntityBundleModalFormShape>
                }
                label="Current beneficiaries & dispositions"
              />
              <FormAwareCheckbox<ExportEntityBundleModalFormShape>
                control={control}
                fieldName={
                  'includeInsurancePolicySlides' as const satisfies PathsOf<ExportEntityBundleModalFormShape>
                }
                label="Insurance policies"
              />
            </FormLayoutItem>
          </FormLayoutRow>
        </Stack>
      </Card>
    </Stack>
  );
}
